import React, {ComponentProps, FC, ReactNode, useEffect} from 'react';
import {animated, useTransition} from 'react-spring';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious, useTimeout} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {getIsCustomer, getIsOnDevelopersPlan} from '../../../store/data/company/companySelectors';
import {navigateTopBarPopoverAction} from '../../../store/panels/app/appPanelActions';
import {AppPanelPopoverTypesEnum} from '../../../store/panels/app/appPanelState';
import {progressBarTransitionDurationInMs} from '../../startGuide/startGuideProgressBar';
import {OnboardingGuideAnimatedStyles, OnboardingGuidePopover} from '../guide/onboardingGuidePopover';
import {SetupGuideScreenTasksAndProgress} from '../tasks/onboardingTypes';
import {SetupGuideHelpCard} from './setupGuideHelpCard';
import {SetupGuideScreen} from './setupGuideScreen';

/*
 * Props.
 */

interface SetupGuidePopoverProps {
  onClose: () => void;
  onDismissPermanentlyClick: () => void;
  styles: OnboardingGuideAnimatedStyles | undefined;
  panelId: AppPanelId;
  children: ReactNode;
  navMenu: ReactNode;
}

export interface SetupGuideBasicScreenProps {
  style: ComponentProps<typeof AnimatedSetupGuideScreen>['style'];
  onInternalTaskStart: () => void;
}

export interface SetupGuideTasksScreenProps extends SetupGuideBasicScreenProps {
  userGivenName: string;
  tasks: SetupGuideScreenTasksAndProgress;
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledNavContainerDiv = styled.div`
  width: 240px;
  height: 692px;
  min-width: 240px;
  background-color: ${(p) => p.theme.greys.shade20};
  display: flex;
  flex-direction: column;
`;

const StyledNavMenuDiv = styled.div`
  display: flex;
  padding: 30px 14px;
  flex-direction: column;
  gap: 30px;
`;

const StyledNavMenuTitleDiv = styled.div`
  display: flex;
  padding: 0px 16px;
  font-size: 24px;
  font-weight: ${fontWeights.black};
  line-height: 130%;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledNavMenuItemsDiv = styled.div`
  display: flex;
  padding: 0px 8px;
  flex-direction: column;
  gap: 8px;
`;

const StyledNavHelpCardContainerDiv = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
`;

const StyledNavDismissContainerDiv = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
`;

const StyledContentContainerDiv = styled.div`
  position: relative;
  flex-grow: 1;
`;

const StyledCloseButtonContainerDiv = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

export const AnimatedSetupGuideScreen = styled(animated(SetupGuideScreen))`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) => p.theme.greys.white};
`;

const prevScreenStyle = {opacity: 0, transform: 'translateY(-10%)'};
const currentScreenStyle = {opacity: 1, transform: 'translateY(0%)'};
const nextScreenStyle = {opacity: 0, transform: 'translateY(10%)'};
const screenTransitionConfig = {mass: 0.25, tension: 250};

/*
 * Component.
 */

export const SetupGuidePopover: FC<SetupGuidePopoverProps> = (props) => {
  const {panelId, children, onClose, styles, onDismissPermanentlyClick, navMenu} = props;

  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const isCustomer = useAppSelector(getIsCustomer);
  const isDeveloper = useAppSelector(getIsOnDevelopersPlan);
  const canDismissPermanently = isCustomer || isDeveloper;

  return (
    <OnboardingGuidePopover onRequestClose={onClose} styles={styles}>
      <StyledNavContainerDiv>
        <StyledNavMenuDiv>
          <StyledNavMenuTitleDiv>
            <Trans id="setup-guide-title">Setup guide</Trans>
          </StyledNavMenuTitleDiv>
          <StyledNavMenuItemsDiv>{navMenu}</StyledNavMenuItemsDiv>
        </StyledNavMenuDiv>
        <StyledNavHelpCardContainerDiv>
          <SetupGuideHelpCard
            onMoreResourcesClick={() => {
              onClose();
              dispatch(navigateTopBarPopoverAction(panelId, AppPanelPopoverTypesEnum.HELP));
            }}
          />
        </StyledNavHelpCardContainerDiv>
        {canDismissPermanently && (
          <StyledNavDismissContainerDiv>
            <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onDismissPermanentlyClick}>
              <Trans id="setup-guide-dismiss-permanently-button">Dismiss permanently</Trans>
            </Button>
          </StyledNavDismissContainerDiv>
        )}
      </StyledNavContainerDiv>
      <StyledContentContainerDiv>
        {children}

        <StyledCloseButtonContainerDiv>
          <IconButton
            iconName="close"
            label={<Trans id="setup-guide-close">Close</Trans>}
            onClick={onClose}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
        </StyledCloseButtonContainerDiv>
      </StyledContentContainerDiv>
    </OnboardingGuidePopover>
  );
};

export function useRenderScreenTransitions<T>(screens: ReadonlyArray<T>, currentScreen: T) {
  const screenIndex = screens.indexOf(currentScreen);
  const previousScreenIndex = usePrevious(screenIndex) ?? screenIndex;
  const currentScreenIndexDiff = screenIndex - previousScreenIndex;

  return useTransition(currentScreen, {
    // Don't animate when the component first mounts.
    from: currentScreenIndexDiff === 0 ? undefined : computeEnterFromScreenStyle(currentScreenIndexDiff),
    enter: currentScreenStyle,
    leave: computeLeaveToScreenStyle(currentScreenIndexDiff),
    config: screenTransitionConfig,
  });
}

function computeEnterFromScreenStyle(screenIndexDiff: number) {
  // When moving to the previous screen, the current screen will become the previous screen.
  return screenIndexDiff < 0 ? prevScreenStyle : nextScreenStyle;
}

function computeLeaveToScreenStyle(screenIndexDiff: number) {
  // When moving to the previous screen, the current screen will become the next screen.
  return screenIndexDiff < 0 ? nextScreenStyle : prevScreenStyle;
}

const automaticallyAdvanceScreenDelay = progressBarTransitionDurationInMs + 500;

// Automatically navigates to the next screen when a set of tasks has been completed.
export function useAutomaticallyAdvanceScreen<T>(
  defaultScreen: T,
  currentScreen: T,
  screenTransition: [T, T] | undefined,
  onScreenChange: (screen: T) => void,
) {
  const [safeSetTimeout] = useTimeout();

  const onScreenAdvance = useEventHandler((fromScreen: T, toScreen: T) => {
    // Wait a few seconds for the progress bar CSS animations to complete.
    safeSetTimeout(() => {
      // Only auto-advance if we're on the right screen and the user hasn't manually navigated to a different screen.
      if (defaultScreen === currentScreen && currentScreen === fromScreen) {
        onScreenChange(toScreen);
      }
    }, automaticallyAdvanceScreenDelay);
  });

  useEffect(() => {
    if (screenTransition) {
      onScreenAdvance(...screenTransition);
    }
  }, [screenTransition, onScreenAdvance]);
}
